import LogoFooterCollapsed from '@assets/svg/brand/seclogic_collapsed_logo_black.svg';
import LogoFooter from '@assets/svg/brand/seclogic_logo_sidebar.svg';
import agent from '@root/agent';
import ShieldNewIconExpanded from '@root/assets/svg/Security/ShieldNewIconExpanded';
import { CHANGE_SIDEBAR_WIDTH } from '@root/constants/actionTypes';
import useEnhancedQuery from '@root/query/useEnhancedQuery';

import { Popover, PopoverContent, PopoverTrigger } from '@root/components/v3/popover';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from '@root/components/v3/sidebar';
import { cn } from '@root/lib/utils';

import { ChevronRight, Menu } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { INVENTORY_TAB_PATHS, inventoryTabPath, resolveInventoryTab } from '@root/constants/inventoryTabs';
import { PREDEFINE_ROUTE_MAP, ROUTES, moduleHasTabs } from './navConfig';

// Constants
const BADGES = {
  'Data Security': 'New',
  'Attack Surface': 'Beta',
  'Kubernetes Live': 'New',
  'Code Security': 'New',
  'AI Security': 'Beta',
  'API Security': 'Alpha',
  FinOps: 'Alpha',
  // 'Database Security': 'New',
};

// Icon map
const getIconClass = name => {
  const iconMap = {
    Dashboard:
      'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    Risk: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z',
    Inventory:
      'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    Insights:
      'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    'Data Security':
      'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    // Radar sweep with an outward arrow: what is being scanned, and what is reachable from outside.
    'Attack Surface': 'M12 21a9 9 0 1 1 9-9M12 17a5 5 0 1 1 5-5M12 12l7-7M14 5h5v5',
    // Lucide "route": an entry point, a winding path, a target.
    'Attack Paths': (
      <>
        <circle cx="6" cy="19" r="3" />
        <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
        <circle cx="18" cy="5" r="3" />
      </>
    ),
    Identity: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    'API Security': `M6.2 7.6H2.6c-.08 0-.15-.07-.15-.15s.07-.15.15-.15h3.6c.08 0 .15.07.15.15s-.07.15-.15.15z
M6.2 6.2H2.6c-.08 0-.15-.07-.15-.15s.07-.15.15-.15h3.6c.08 0 .15.07.15.15s-.07.15-.15.15z
M6.2 4.8H2.6c-.08 0-.15-.07-.15-.15s.07-.15.15-.15h3.6c.08 0 .15.07.15.15s-.07.15-.15.15z
M18.1 1.6a.3.3 0 11-.6 0 .3.3 0 01.6 0z
M16.9 1.6a.3.3 0 11-.6 0 .3.3 0 01.6 0z
M15.7 1.6a.3.3 0 11-.6 0 .3.3 0 01.6 0z
M9.4 14.4H.8c-.12 0-.2-.08-.2-.2V.8c0-.12.08-.2.2-.2h18.4c.12 0 .2.08.2.2v13.8h-.4V2.6H1v11.6h8.4v.2z
M1 2.2h18V1H1v1.2z
M14.2 19.4c-2 0-3.6-1.6-3.6-3.6v-4.2l3.6-1.8 3.6 1.8v4.2c0 2-1.6 3.6-3.6 3.6z
M11 11.8v4c0 1.8 1.4 3.2 3.2 3.2s3.2-1.4 3.2-3.2v-4l-3.2-1.6-3.2 1.6z
`,
    Workload:
      'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6',
    Threat: (
      <g transform="translate(0.5 0) scale(1.15)">
        <path d="M10 3.8 L 17 16 L 3 16 Z" />
        <line x1="10" y1="8.2" x2="10" y2="11.8" />
        <circle cx="10" cy="14" r="0.75" fill="#9333ea" stroke="none" />
      </g>
    ),
    'Code Security':
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    'Kubernetes Live':
      'M12,2 L14,3 L15,5 L17,6 L18,8 L17,10 L18,12 L17,14 L15,15 L14,17 L12,18 L10,17 L9,15 L7,14 L6,12 L7,10 L6,8 L7,6 L9,5 L10,3 Z M12,8 A4,4 0 1,0 12,12 A4,4 0 1,0 12,8',
    'Remediation Hub':
      'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    Explorer: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    'Application Security': (
      <>
        <path d="M14 3H5a2 2 0 00-2 2v14a2 2 0 002 2h7"></path>
        <path d="M3 8h11"></path>
        <path d="M18 21s4-2 4-5v-3l-4-1.5L14 13v3c0 3 4 5 4 5z"></path>
      </>
    ),
    Workspace:
      'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    'AI Security':
      'M12 2l1.8 4.2L18 8l-4.2 1.8L12 14l-1.8-4.2L6 8l4.2-1.8L12 2zM4 14l.8 2L7 17l-2.2.8L4 20l-.8-2L1 17l2.2-.8L4 14zM19 13l.7 1.7L21 15l-1.3.7L19 17l-.7-1.3L17 15l1.3-.7L19 13z',
    'Database Security': (
      <>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5V19A9 3 0 0 0 21 19V5" />
        <path d="M3 12A9 3 0 0 0 21 12" />
      </>
    ),
    FinOps: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
        <path d="M12 18V6" />
      </>
    ),
  };
  return iconMap[name] || iconMap.Dashboard;
};

const renderIcon = name => {
  const icon = getIconClass(name);
  return typeof icon === 'string' ? <path d={icon} /> : icon;
};

// Helper to check if child is active
const isChildActive = (children, selectedChild) => {
  return children?.some(child => child.to === selectedChild);
};

// NavItem Component - WITH CENTRALIZED STATE CONTROL
const NavItem = memo(({ route, isSelected, selectedChild, isOpen, onOpen, onClose, onCloseImmediate }) => {
  const { state } = useSidebar();
  const triggerRef = useRef(null);

  const childCount = route.children?.length || 0;
  // Tab-eligible modules (see moduleHasTabs) expose their children as header tabs, so the sidebar
  // shows them as a single direct link (to the first child) instead of a children popover.
  const showsTabs = moduleHasTabs(route);
  const usePopover = childCount > 0 && !showsTabs;
  const isCollapsed = state === 'collapsed';
  const badge = BADGES[route.name];

  // Highlight the module as active whenever one of its children is the current route, regardless
  // of whether those children render as a popover or as header tabs.
  const hasActiveChild = childCount > 0 && isChildActive(route.children, selectedChild);
  const isActive = isSelected || hasActiveChild;
  // Tabbed modules always land on their first tab; leaf modules use their own `to`.
  const linkTo = showsTabs ? route.children[0].to : route.to || '#';

  // Calculate bridge position
  const [bridgeStyle, setBridgeStyle] = useState({});

  useEffect(() => {
    if (isOpen && triggerRef.current && usePopover) {
      const rect = triggerRef.current.getBoundingClientRect();
      setBridgeStyle({
        position: 'fixed',
        top: `${rect.top - 4}px`, // Slight extension above
        left: `${rect.right}px`,
        width: '24px', // Wide enough to cover 12px gap + extra safety
        height: `${rect.height + 8}px`, // Slight extension below
        zIndex: 9998,
        pointerEvents: 'auto',
        // Uncomment to visualize bridge for debugging:
        // backgroundColor: 'rgba(255, 0, 0, 0.2)',
      });
    }
  }, [isOpen, usePopover]);

  // RULE: non-tab modules with children -> Popover of children. Tab-eligible modules render their
  // children as header tabs instead, so they fall through to the simple direct link below.
  if (usePopover) {
    return (
      <>
        <Popover
          open={isOpen}
          onOpenChange={open => {
            if (!open) {
              onClose();
            }
          }}>
          <PopoverTrigger asChild>
            <div
              ref={triggerRef}
              className={cn(
                'flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition-colors cursor-pointer',
                isActive ? 'bg-purple-50 text-purple-600' : 'text-gray-600 hover:bg-gray-50 hover:text-[#9333EA]',
                isCollapsed && 'justify-center px-2',
              )}
              onMouseEnter={onOpen}
              onMouseLeave={onClose}>
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24">
                {renderIcon(route.name)}
              </svg>
              {!isCollapsed && (
                <>
                  <span className="font-medium text-sm flex-1 hover:text-[#9333EA]">{route.name}</span>
                  {badge && (
                    <span
                      className={cn(
                        'text-[8px] font-bold uppercase px-1.5 py-0.5 rounded',
                        badge === 'New' ? 'text-green-600' : 'text-blue-500',
                      )}>
                      {badge}
                    </span>
                  )}
                  <ChevronRight size={14} className="text-gray-400 shrink-0 ml-auto" />
                </>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={12}
            alignOffset={0}
            collisionPadding={16}
            className="w-52 p-2 border border-gray-200 shadow-xl bg-white rounded-lg"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            onOpenAutoFocus={e => e.preventDefault()}
            onPointerDownOutside={e => e.preventDefault()}
            onInteractOutside={e => e.preventDefault()}
            style={{ zIndex: 9999 }}>
            <div className="space-y-1">
              {isCollapsed && (
                <div className="px-3 py-2 font-semibold text-sm text-gray-900  border-gray-100 mb-2">{route.name}</div>
              )}
              {route.children.map(child => (
                <Link
                  key={child.to}
                  to={child.to}
                  onClick={onCloseImmediate}
                  style={{ textDecoration: 'none' }}
                  className={cn(
                    'block px-3 py-2 rounded-lg text-sm transition-colors hover:text-[#9333EA]',
                    selectedChild === child.to
                      ? 'bg-purple-50 text-purple-600 font-medium hover:text-[#9333EA]'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-[#9333EA]',
                  )}>
                  {child.name}
                  {child.badge && (
                    <span className="ml-2 text-[8px] font-bold uppercase text-blue-500">{child.badge}</span>
                  )}
                </Link>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Invisible Bridge - keeps popover open while traversing */}
        {isOpen && <div style={bridgeStyle} onMouseEnter={onOpen} onMouseLeave={onClose} />}
      </>
    );
  }

  // No popover - simple link (leaf modules, and 2-3 child modules that use header tabs)
  const linkContent = (
    <Link
      to={linkTo}
      style={{ textDecoration: 'none' }}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition-colors relative hover:text-[#9333EA]',
        isActive ? 'bg-purple-50 text-purple-600' : 'text-gray-600 hover:bg-gray-50 hover:text-[#9333EA]',
        isCollapsed && 'justify-center px-2',
      )}>
      <svg
        className="w-4 h-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        viewBox="0 0 24 24">
        {renderIcon(route.name)}
      </svg>
      {!isCollapsed && (
        <>
          <span className="font-medium text-sm flex-1 hover:text-[#9333EA]">{route.name}</span>
          {badge && (
            <span
              className={cn(
                'text-[8px] font-bold uppercase px-1.5 py-0.5 rounded',
                badge === 'New' ? 'text-green-600' : 'text-blue-500',
              )}>
              {badge}
            </span>
          )}
        </>
      )}
    </Link>
  );

  // When collapsed, only the icon is visible - surface the module name using the same popover UI
  // as the children popover, on hover.
  if (isCollapsed) {
    return (
      <Popover
        open={isOpen}
        onOpenChange={open => {
          if (!open) {
            onClose();
          }
        }}>
        <PopoverTrigger asChild>
          <div onMouseEnter={onOpen} onMouseLeave={onClose}>
            {linkContent}
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          alignOffset={0}
          collisionPadding={16}
          className="w-fit p-2 border border-gray-200 shadow-xl bg-white rounded-lg"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onOpenAutoFocus={e => e.preventDefault()}
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          style={{ zIndex: 9999 }}>
          <div className="px-3 py-2 font-semibold text-sm text-gray-900 border-gray-100">{route.name}</div>
        </PopoverContent>
      </Popover>
    );
  }

  return linkContent;
});

NavItem.displayName = 'NavItem';

// Main Sidebar Content - WITH CENTRALIZED POPOVER STATE
const SidebarContentComponent = memo(() => {
  const location = useLocation();
  const [openPopover, setOpenPopover] = useState(null); // Track which popover is open
  const closeTimeoutRef = useRef(null);

  const fullPath = location.pathname;
  const basePath = '/' + fullPath.split('/')[1];
  const routeLabel = PREDEFINE_ROUTE_MAP[basePath] || 'Unknown';

  const selectedChild = useMemo(() => {
    // Inventory is one route with query-param tabs, so its active child comes from `?tab=`.
    // The resource table is a drill-down of the Resources tab and highlights it.
    if (basePath === '/inventory') {
      if (fullPath.includes('resources-table')) return INVENTORY_TAB_PATHS.resources;
      return inventoryTabPath(resolveInventoryTab(location.search));
    }
    if (fullPath.includes('/kubernetes-live/compliance')) return '/kubernetes-live/compliance';
    if (fullPath.includes('/kubernetes-live/inventory')) return '/kubernetes-live/inventory';
    if (fullPath.includes('/kubernetes-live/events')) return '/kubernetes-live/events?isKubernetes=true';
    if (fullPath.includes('/kubernetes-live/risk-acceptance')) return '/kubernetes-live/risk-acceptance';

    // API Security sub-routes — return the full path so they match their own children
    if (fullPath.startsWith('/api-security/')) return fullPath;

    if (fullPath.includes('/compliance')) return '/compliance';
    if (fullPath.includes('/identity/overview')) return '/identity/overview';
    if (fullPath.includes('/risk')) return '/risk';
    if (fullPath.includes('/rules')) return '/rules';
    if (fullPath.includes('/vulnerability/management')) return '/vulnerability/management';
    if (fullPath.includes('/notification')) return '/notifications';
    return fullPath;
  }, [basePath, fullPath, location.search]);

  // Centralized popover control - ensures only ONE popover open at a time
  const handlePopoverOpen = useCallback(routeName => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenPopover(routeName);
  }, []);

  const handlePopoverClose = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenPopover(null);
    }, 150);
  }, []);

  const handlePopoverCloseImmediate = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenPopover(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <SidebarContent className="px-2 py-4">
      <SidebarMenu className="gap-1">
        {ROUTES.map(route => (
          <SidebarMenuItem key={route.name}>
            <NavItem
              route={route}
              isSelected={routeLabel === route.name}
              selectedChild={selectedChild}
              isOpen={openPopover === route.name}
              onOpen={() => handlePopoverOpen(route.name)}
              onClose={handlePopoverClose}
              onCloseImmediate={handlePopoverCloseImmediate}
            />
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarContent>
  );
});

SidebarContentComponent.displayName = 'SidebarContentComponent';

// Progress Bar Component
const ProgressBar = memo(({ configData, isFetching }) => {
  const history = useHistory();
  const { state } = useSidebar();
  const percentage = parseInt(configData?.data?.percentage) ?? 0;

  if (state === 'collapsed') return null;

  return (
    <div
      className={cn(
        'mx-2 mb-2 p-2.5 bg-purple-50 rounded-lg border border-gray-200 cursor-pointer transition-colors hover:bg-purple-100',
        isFetching && 'animate-pulse',
      )}
      onClick={() => history.push('/progress')}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-600">Finish Setup</span>
        <span className="text-xs font-bold text-purple-600">{percentage}%</span>
      </div>
      <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-600 to-purple-500 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
});

ProgressBar.displayName = 'ProgressBar';

// Sidebar Header Component
const SidebarHeaderComponent = memo(() => {
  const history = useHistory();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <SidebarHeader className=" border-gray-200 p-3">
      <div className="flex items-center justify-between">
        {!isCollapsed && (
          <div className="flex items-center cursor-pointer" onClick={() => history.push('/dashboard')}>
            <ShieldNewIconExpanded />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className={cn(' hover:bg-gray-100 rounded-lg transition-colors', isCollapsed && 'mx-auto')}>
          <Menu size={1} className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </SidebarHeader>
  );
});

SidebarHeaderComponent.displayName = 'SidebarHeaderComponent';

// Sidebar Footer Component
const SidebarFooterComponent = memo(() => {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <SidebarFooter className=" border-gray-200 p-3">
      <div className="flex justify-center">
        {isCollapsed ? (
          <img src={LogoFooterCollapsed} alt="SecLogic" className="h-[18px]" />
        ) : (
          <img src={LogoFooter} alt="SecLogic" className="h-[18px]" />
        )}
      </div>
    </SidebarFooter>
  );
});

SidebarFooterComponent.displayName = 'SidebarFooterComponent';

// Main Component
const VerticalNavbarNew = memo(() => {
  // const history = useHistory();
  const dispatch = useDispatch();

  const { data: configData, isFetching: isFetchingConfig } = useEnhancedQuery(['get_config'], () =>
    agent.Tenant_Config.get_config(),
  );

  const sidebarWrapperRef = useCallback(
    node => {
      if (!node) return;

      const resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width) {
          dispatch({ type: CHANGE_SIDEBAR_WIDTH, payload: width });
        }
      });

      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
    },
    [dispatch],
  );

  return (
    <div ref={sidebarWrapperRef}>
      <SidebarProvider defaultOpen={true}>
        <Sidebar collapsible="icon" className="border-r border-gray-200">
          <SidebarHeaderComponent />

          <SidebarContentComponent />

          <ProgressBar configData={configData} isFetching={isFetchingConfig} />

          <SidebarFooterComponent />
        </Sidebar>
      </SidebarProvider>
    </div>
  );
});

VerticalNavbarNew.displayName = 'VerticalNavbarNew';

export default VerticalNavbarNew;
